<template>
  <div>
    <AppModal
      v-if="showDocStatusModal"
      ref="AppModal"
      data-testid="documents-status-modal"
      class="downloading-modal"
    >
      <template slot="header">
        <v-icon @click="$emit('close')" class="text-right closeBtn"
          >mdi-close</v-icon
        >
        <div v-if="applicationPDFUnavailable !== 'ALL'" class="heading">
          <span class="mr-2">
            <v-icon color="primary">mdi-lock-outline</v-icon>
          </span>
          <span class="font-weight-bold"
            >Please keep these applications secure</span
          >
        </div>
        <div v-if="applicationPDFUnavailable === 'ALL'" class="heading">
          <span class="mr-2">
            <v-icon color="primary">mdi-alert-outline</v-icon>
          </span>
          <span class="font-weight-bold"
            >This application is unavailable for download</span
          >
        </div>
      </template>

      <div slot="body">
        <div class="content">
          <p v-if="applicationPDFUnavailable !== 'ALL'">
            Please ensure once you download this document that it is kept in a
            secure location within the school premises.
          </p>
          <p v-if="applicationPDFUnavailable === 'ALL'">
            Sorry, the PDF of this application cannot be downloaded as it is
            currently missing.
          </p>
          <div
            v-if="applicationPDFUnavailable === 'FEW'"
            data-testid="docs-unavailable-msg"
          >
            <div class="sub-header mb-4">
              <span class="mr-2">
                <v-icon color="primary">mdi-alert-outline</v-icon>
              </span>
              <span class="font-weight-bold"
                >{{ applicationPDFUnavailableCount }} documents are unavailable
                for download</span
              >
            </div>
            <p>
              The following documents are unavailable because they are currently
              missing:
            </p>
          </div>
          <div
            v-for="(supportingDocument, i) in supportingDocuments"
            :key="i"
            class="body-1 my-0 py-0 mb-5 docs-item vc"
          >
            <div class="font-weight-bold mb-4 docs-item-title">
              {{ supportingDocument.name }}
            </div>
            <div class="mb-4 docs-item-content">
              <div v-if="supportingDocument.status === 'MISSING'">
                <div class="mb-1">
                  <span class="mr-2">
                    <v-icon color="primary">mdi-file-outline</v-icon>
                  </span>
                  <span class="font-weight-bold">
                    {{ `${supportingDocument.applicationID}.pdf` }}
                  </span>
                </div>
                <div>
                  <span class="mr-2">
                    <v-icon color="red darken-1">mdi-alert-circle</v-icon>
                  </span>
                  <span>{{ docMessageByStatus('MISSING') }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="fileUnavailable && applicationPDFUnavailable !== 'ALL'"
            data-testid="docs-unavailable-msg"
          >
            <div class="sub-header mb-4">
              <span class="mr-2">
                <v-icon color="primary">mdi-alert-outline</v-icon>
              </span>
              <span class="font-weight-bold"
                >Some supporting documents are unavailable</span
              >
            </div>
            <p>
              Some of the supporting documents from the selected applications
              cannot be downloaded as they may be infected, or are currently
              missing. The documents are listed below:
            </p>
          </div>
          <div
            v-for="(supportingDocument, i) in supportingDocuments"
            :key="i"
            class="body-1 my-0 py-0 mb-2 docs-item vc"
          >
            <div class="font-weight-bold mb-4 docs-item-title">
              {{ supportingDocument.name }}
            </div>
            <div
              v-for="(doc, j) in supportingDocument.docs"
              :key="j"
              class="mb-4 docs-item-content"
            >
              <div v-if="doc.status !== 'SAFE'">
                <div class="mb-1">
                  <span class="mr-2">
                    <v-icon color="primary">mdi-file-outline</v-icon>
                  </span>
                  <span class="font-weight-bold">{{
                    fileName(doc.objectKey)
                  }}</span>
                </div>
                <div>
                  <span class="mr-2">
                    <v-icon color="red darken-1">mdi-alert-circle</v-icon>
                  </span>
                  <span>{{ docMessageByStatus(doc.status) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="applicationPDFUnavailable !== 'ALL'" slot="footer">
        <v-btn x-large @click="startDownload">
          <v-icon class="mr-2">mdi-download-outline</v-icon>
          Download
        </v-btn>
      </div>
    </AppModal>
    <AppModal
      v-if="showDownloadProgressModal"
      ref="AppModal"
      data-testid="download-documents-modal"
      class="downloading-modal"
    >
      <span slot="header">
        <v-avatar size="40" color="primary lighten-4" class="mr-4">
          <v-icon color="primary">mdi-download-outline</v-icon>
        </v-avatar>
        <span class="font-weight-bold"
          >Preparing {{ count }} applications for download</span
        >
      </span>

      <div slot="body">
        <div class="content">
          <v-progress-linear
            indeterminate
            color="primary lighten-1"
            class="mt-2 mb-6"
          ></v-progress-linear>

          <p>
            We are preparing a zip archive of the applications you have
            selected. This window will <strong>close automatically</strong> once
            the download commences.
          </p>
          <p>
            <strong>Note:</strong> for a large number of applications, this may
            take several minutes.
          </p>
        </div>
      </div>

      <div slot="footer">
        <v-btn x-large @click="$emit('close')">
          <v-icon class="mr-2">mdi-delete-outline</v-icon>
          Cancel download
        </v-btn>
      </div>
    </AppModal>
    <AppModal
      v-if="downloadError"
      ref="AppModal"
      data-testid="download-error-modal"
    >
      <template slot="header">
        <v-icon @click="$emit('close')" class="text-right closeBtn"
          >mdi-close</v-icon
        >
        <div class="heading px-3">
          <v-avatar size="40" color="primary lighten-4" class="mr-4">
            <v-icon color="primary">mdi-download-outline</v-icon>
          </v-avatar>
          <span class="font-weight-bold">
            Error downloading application
          </span>
        </div>
      </template>
      <template slot="body">
        <div class="content mt-3 mx-3">
          <p>
            Sorry, the application you selected cannot be downloaded at this
            time. Please try again later.
          </p>
          <p>
            If this problem persists, contact support on
            <strong></strong>.
          </p>
        </div>
      </template>
    </AppModal>
  </div>
</template>

<script>
import { FILE_STATUSES } from '@/constants'
import AppModal from '@/components/app/AppModal.vue'

function getTransformedDocs(supportingDocuments, applicationID) {
  let docs = []

  // TODO scope for future enhancements. Now OOA only supports MED considerations
  if (
    supportingDocuments &&
    supportingDocuments['MED'] &&
    supportingDocuments['MED'].length > 0
  ) {
    docs = supportingDocuments['MED'].map(doc => ({
      objectKey: applicationID + doc.objectKey
    }))
  }

  return docs
}

export default {
  name: 'EaDownloadDocuments',
  components: {
    AppModal
  },
  props: {
    applications: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      showDocStatusModal: true,
      showDownloadProgressModal: false,
      hasCancelled: false,
      downloadError: false,
      supportingDocuments: [],
      downloadApplications: [],
      applicationPDFUnavailableCount: 0
    }
  },
  computed: {
    count() {
      return this.downloadApplications ? this.downloadApplications.length : 0
    },
    applicationPDFUnavailable() {
      let status = 'NONE'
      let count = 0

      if (this.supportingDocuments.length > 0) {
        for (let i = 0; i < this.supportingDocuments.length; i++) {
          const doc = this.supportingDocuments[i]
          if (doc.status === 'MISSING') {
            count += 1
          }
        }
      }

      if (count !== 0) {
        status = count === this.supportingDocuments.length ? 'ALL' : 'FEW'
        this.applicationPDFUnavailableCount = count
      }

      return status
    },
    fileUnavailable() {
      let isFileUnavailable = false

      if (this.supportingDocuments.length > 0) {
        for (let i = 0; i < this.supportingDocuments.length; i++) {
          const doc = this.supportingDocuments[i]
          if (doc.fileUnavailable) {
            isFileUnavailable = true
            break
          }
        }
      }

      return isFileUnavailable
    }
  },
  methods: {
    init() {
      this.initDocStatus()
    },
    initDocStatus() {
      this.$store.dispatch('showSpinner')

      const applicationsInfo = this.applications.map(app => ({
        applicationID: app.applicationID,
        docs: getTransformedDocs(app.supportingDocuments, app.applicationID)
      }))
      this.$store
        .dispatch('getAppsDocStatus', {
          applicationsInfo
        })
        .then(data => {
          this.supportingDocuments = data.supportingDocuments.map(app => {
            const selApp = this.applications.filter(
              a => a.applicationID === app.applicationID
            )
            if (selApp.length > 0) {
              app.name =
                selApp[0].studentFamilyName + ',' + selApp[0].studentFirstName
              app.studentFirstName = selApp[0].studentFirstName
              app.studentFamilyName = selApp[0].studentFamilyName
            }
            app.fileUnavailable = false
            for (let i = 0; i < app.docs.length; i++) {
              const doc = app.docs[i]
              if (doc.status !== FILE_STATUSES.SAFE) {
                app.fileUnavailable = true
              }
            }
            return app
          })
          this.$store.dispatch('hideSpinner')
        })
        .catch(error => {
          if (error) {
            this.downloadError = true
            this.$store.dispatch('hideSpinner')
          }
        })
    },
    docMessageByStatus(status) {
      let msg = 'This file cannot be downloaded.'

      if (status === FILE_STATUSES.INFECTED) {
        msg = 'This file is infected and cannot be downloaded.'
      } else if (status === FILE_STATUSES.TO_BE_SCANNED) {
        msg = 'This file has not been virus scanned and cannot be downloaded.'
      } else if (status === FILE_STATUSES.MISSING) {
        msg = 'This file is missing and cannot be downloaded.'
      }
      return msg
    },
    fileName(objectKey) {
      return objectKey.split('/').slice(-1)[0]
    },
    startDownload() {
      this.showDocStatusModal = false
      this.hasCancelled = false
      this.downloadApplications = this.supportingDocuments.map(app => {
        let supportingDocuments = app.docs
          .filter(doc => doc.status === FILE_STATUSES.SAFE)
          .map(doc => doc.objectKey)
        supportingDocuments =
          supportingDocuments && supportingDocuments.length > 0
            ? supportingDocuments
            : undefined
        return {
          id: app.applicationID,
          status: app.status,
          studentFirstName: app.studentFirstName,
          studentFamilyName: app.studentFamilyName,
          supportingDocuments
        }
      })

      this.downloadApplications = this.downloadApplications.filter(
        downApp => downApp.status !== 'MISSING'
      )

      if (
        !this.downloadApplications ||
        this.downloadApplications.length === 0
      ) {
        return
      }

      if (this.downloadApplications.length >= 10) {
        this.showDownloadProgressModal = true
      }

      this.$store
        .dispatch('getSupportingDocumentsPDFs', {
          downloadApplications: this.downloadApplications
        })
        .then(data => {
          if (!this.hasCancelled) {
            if (data.body) {
              // Set an artificial delay to avoid AWS access denied error FUS-332
              // This is hopefully a temporary work-around until we can find and fix the underlying cause
              // where the zip is not actually available in S3 when the signed url is returned.
              const dynamicTimeout =
                Math.floor(this.downloadApplications.length / 10) * 100 || 500
              setTimeout(() => {
                this.doDownload(
                  data.body.Location,
                  'Out of Area Enrolments (multiple)'
                )
              }, dynamicTimeout)
            } else {
              const pdfData = `data:application/pdf;base64,${data}`
              const fileName = `${this.downloadApplications[0]['id']}`
              // Workaround as IE prevents blob URIs due to security restrictions
              // stackoverflow.com/a/45732897
              if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                const pdfDataBlob = this.pdfBlobConversion(pdfData)
                window.navigator.msSaveOrOpenBlob(
                  pdfDataBlob,
                  `${fileName}.pdf`
                )
                this.$emit('close')
                return
              }
              this.doDownload(pdfData, `${fileName}.pdf`)
            }
          }
        })
        .catch(error => {
          if (error) {
            this.downloadError = true
          }
        })
    },
    doDownload(href, fileName) {
      const link = document.createElement('a')

      link.href = href
      link.download = fileName
      link.target = '_blank'
      link.click()

      this.$emit('close')
    },
    // base64-blob library not working
    // From stackoverflow.com/a/48909758
    pdfBlobConversion(b64Data, contentType) {
      contentType = contentType || ''
      var sliceSize = 512
      b64Data = b64Data.replace(/^[^,]+,/, '')
      b64Data = b64Data.replace(/\s/g, '')
      var byteCharacters = window.atob(b64Data)
      var byteArrays = []

      for (
        var offset = 0;
        offset < byteCharacters.length;
        offset = offset + sliceSize
      ) {
        var slice = byteCharacters.slice(offset, offset + sliceSize)

        var byteNumbers = new Array(slice.length)
        for (var i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i)
        }

        var byteArray = new Uint8Array(byteNumbers)

        byteArrays.push(byteArray)
      }

      var blob = new Blob(byteArrays, { type: contentType })
      return blob
    }
  },
  mounted() {
    this.init()
  }
}
</script>

<style lang="scss" scoped>
::v-deep .AppModal {
  text-align: left;
  white-space: normal;

  .box {
    padding: 1rem;
    max-width: 722px;
  }

  .heading {
    font-size: 1.3333rem;
  }

  .body {
    font-size: 1.0666rem;
    line-height: 1.6rem;
    .sub-header {
      font-size: 1.25rem;
    }
  }

  .docs-item {
    background: $grey-light-10;
    border-radius: 4px;
    padding: 10px 20px !important;

    .docs-item-title {
      font-size: 1.125rem;
    }

    .docs-item-content {
      font-size: 0.875rem;
    }
  }
}

::v-deep .header {
  flex-direction: column;
}

.heading {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
}

.closeBtn {
  margin: -0.25rem -0.5rem 0 auto;
  border: none;
  padding: 0;
  &:hover {
    text-decoration: none !important;
  }
}

.downloading-modal ::v-deep .header {
  display: flex;
  align-items: flex-start;
}
</style>
